// src/billing/refusalCopy.js — backend billing refusal → our sentence.
//
// Branch on the CODE, never on the message: the message is the server's English, and the first thing a user saw
// on 2026-09-04 was "Plan starter has no monthly price…" verbatim. Used by the Piani screen only. It is its own
// module (not inside a page) so components can import it without dragging a page's router graph into their tests.
//
// ⛔ CUSTOMER_ADMIN_REQUIRED / NO_CUSTOMER are deliberately NOT in the table: callers catch them BEFORE calling
// this and hide the surface ("not for you"), never word them as an error.
// ⭐ v203: a 5xx without a mapped code quotes `requestId` (billingApi keeps it); no status and no code = the
// network; `rowReasonCopy` words an unavailable row from its `reason_code` (never the row's English `reason`);
// `isHandledElsewhere` recognises the errors apiFetch already handled (sign-out, tenant picker, SA banner).
// ⭐ v204 «Passa ora» outcomes are COMPOSED (`changeOutcome`): the 3-D Secure deadline, where the payment link is
// (the button, or only the email), and the scheduled change a failed change released BEFORE the charge
// (`released_scheduled_plan_id` on both 402s and on 502 CHANGE_FAILED_CHANGE_RELEASED — it does not come back).
import { PACK_REASON_KEY } from '../components/billing/PacksPanel.jsx';
import { formatRenewalDate, formatRenewalTime } from '../utils/creditsFormat.js';

/**
 * Backend billing refusal → i18n key.
 *
 * ⭐ The table carries the codes that have a sentence of their own (a remedy, or a fact the user should know).
 * Everything else falls through to `errors.genericWithCode`, which still shows the code so support has a handle —
 * ⛔ but never the server's English, which is worded per route and not for end users.
 */
export const REFUSAL_KEY = {
  // The one refusal the customer fixes themselves — link to /billing-details.
  CUSTOMER_NOT_INVOICEABLE: 'errors.customerNotInvoiceable',
  // Self-serviceable: deactivating people frees seats (the cap is per COMPANY).
  USER_COUNT_OVER_PLAN: 'errors.userCountOverPlan',
  // ⚠️ A dead end for the customer: a retired workspace keeps its seat.
  TENANT_COUNT_OVER_PLAN: 'errors.tenantCountOverPlan',
  // ⛔ The card did not clear and NOTHING CHANGED — offer a card update, not a retry.
  PAYMENT_NOT_COMPLETED: 'errors.paymentNotCompleted',
  SUBSCRIPTION_ALREADY_ACTIVE: 'errors.subscriptionAlreadyActive',
  NO_SUBSCRIPTION: 'errors.noSubscription',
  // An operator-assigned plan — "managed by your administrator".
  NOT_A_STRIPE_SUBSCRIPTION: 'errors.notAStripeSubscription',
  PLAN_CHANGE_NEEDS_SALES: 'errors.planChangeNeedsSales',
  NO_PAYMENT_ACCOUNT: 'errors.noPaymentAccount',
  // A plan change never undoes a scheduled cancellation: «Riprendi» first.
  CANCELLATION_PENDING: 'errors.cancellationPending',
  // The scheduled change was released, then Stripe refused the cancel: re-read, cancel again.
  CANCEL_FAILED_CHANGE_RELEASED: 'errors.cancelFailedChangeReleased',
  PLAN_UNCHANGED: 'errors.planUnchanged',
  // The row/pack the page showed is no longer on sale: the page re-reads.
  UNKNOWN_PLAN: 'errors.staleRow',
  UNKNOWN_PACK: 'errors.staleRow',
  PLAN_NOT_SELLABLE: 'errors.staleRow',
  PLAN_NOT_PURCHASABLE: 'errors.staleRow',
  // v204: a row that went stale under the preview, or a quote that kept failing (the page re-previews up to 3 times).
  CHANGE_NOT_IMMEDIATE: 'errors.staleRow',
  QUOTE_EXPIRED: 'errors.staleRow',
  QUOTE_INVALID: 'errors.staleRow',
  NO_SCHEDULED_CHANGE: 'errors.noScheduledChange',
  // v221: normally handled IN the confirmation (Piani keeps it open with the new texts); a fallback sentence only.
  TERMS_VERSION_CHANGED: 'errors.termsChanged',
  PACK_REQUIRES_PAID_PLAN: PACK_REASON_KEY.PACK_REQUIRES_PAID_PLAN,
  // Catalogue/operator problems the customer cannot fix.
  CURRENT_PLAN_NOT_MAPPED: 'errors.changeUnavailable',
  UNSUPPORTED_PERIOD: 'errors.changeUnavailable',
  // A body the backend refused (e.g. a cancel comment over the limit) — nothing happened.
  BRIEF_VALIDATION_ERROR: 'errors.invalidRequest',
  // Environment facts, not user errors.
  STRIPE_NOT_CONFIGURED: 'errors.stripeNotConfigured',
  STRIPE_ERROR: 'errors.stripeError',
  INVOICE_FOOTER_MISSING: 'errors.invoiceFooterMissing',
};

/** apiFetch already acted on these (sign-out, tenant picker, superadmin banner): say nothing more. */
const HANDLED_MESSAGES = new Set(['SESSION_EXPIRED', 'TENANT_DENIED', 'SUPERADMIN_TENANT_BLOCKED']);
export function isHandledElsewhere(err) {
  return !!err && !err.code && HANDLED_MESSAGES.has(err.message);
}

/**
 * @param {(key: string, vars?: object) => string} t  the `billing` translator
 * @param {Error & {code?: string, status?: number, requestId?: string}} err
 * @param {{justResumed?: boolean, linkUsable?: boolean, releasedPlanName?: string, currentPlanName?: string}} [ctx]
 *   `justResumed`: within RESUME_GRACE_MS of a successful «Riprendi»; the rest: see `changeOutcome`
 *
 * ⛔ An unknown code does NOT fall back to `err.message`. That was the original design, meaning to keep a new
 * backend code from being swallowed — but the message is ALWAYS present (billingApi's structuredError supplies
 * "/checkout-session failed: 500" when the envelope has none), so the fallback was unreachable and every unmapped
 * refusal put the server's English, or an internal string, in front of the user.
 */
export function refusalCopy(t, err, ctx = {}) {
  if (err?.code === 'CANCELLATION_PENDING' && ctx.justResumed) return t('errors.resumeSettling');
  if (CHANGE_OUTCOMES.has(err?.code)) return changeOutcome(t, err, ctx);
  const key = REFUSAL_KEY[err?.code];
  if (key) return t(key);
  if (!err) return t('errors.generic');
  if (err.status == null && !err.code) return t('errors.network');
  if (err.status >= 500 && err.requestId) return t('errors.server', { ref: err.requestId });
  return err.code ? t('errors.genericWithCode', { code: err.code }) : t('errors.generic');
}

const CHANGE_OUTCOMES = new Set([
  'PAYMENT_ACTION_REQUIRED', 'PAYMENT_NOT_COMPLETED', 'PAYMENT_PENDING', 'CHANGE_FAILED_CHANGE_RELEASED',
]);

/**
 * The four «Passa ora» outcomes that carry extras.
 * ctx: `linkUsable` — the outcome shows «Conferma il pagamento» (the link passed billing/stripeLink.js);
 * `releasedPlanName` / `currentPlanName` — names resolved from the RE-READ screen (the released plan falls back to
 * its id; the current plan to «il piano attuale»).
 */
function changeOutcome(t, err, { linkUsable = false, releasedPlanName = null, currentPlanName = null } = {}) {
  const released = err.released_scheduled_plan_id || null;
  const releasedName = releasedPlanName || released;
  const emailLine = t(linkUsable ? 'errors.paymentLinkAlsoEmailed' : 'errors.paymentLinkEmailed');
  const parts = [];
  switch (err.code) {
    case 'PAYMENT_ACTION_REQUIRED': {
      const date = formatRenewalDate(err.expires_at);
      const time = formatRenewalTime(err.expires_at);
      parts.push(date && time ? t('errors.paymentActionRequired', { date, time }) : t('errors.paymentActionRequiredNoDate'));
      parts.push(emailLine);
      if (released) parts.push(t('outcome.scheduledReleasedFinal', { plan: releasedName }));
      break;
    }
    case 'PAYMENT_NOT_COMPLETED':
      parts.push(t('errors.paymentNotCompleted'));
      if (released) parts.push(t('outcome.scheduledReleased', { plan: releasedName }));
      break;
    case 'PAYMENT_PENDING':
      parts.push(t('errors.paymentPending'));
      if (!linkUsable) parts.push(emailLine);
      break;
    default:   // CHANGE_FAILED_CHANGE_RELEASED
      parts.push(currentPlanName
        ? t('errors.changeFailedChangeReleased', { current: currentPlanName })
        : t('errors.changeFailedChangeReleasedNoName'));
      if (released) parts.push(t('outcome.scheduledReleased', { plan: releasedName }));
  }
  return parts.join(' ');
}

/** Why an `unavailable` row has no button — from `reason_code`. null = say nothing (the current plan). */
const ROW_REASON_KEY = {
  CUSTOMER_NOT_INVOICEABLE: 'errors.customerNotInvoiceable',
  USER_COUNT_OVER_PLAN: 'errors.userCountOverPlan',
  TENANT_COUNT_OVER_PLAN: 'errors.tenantCountOverPlan',
  PLAN_CHANGE_NEEDS_SALES: 'errors.planChangeNeedsSales',
  CANCELLATION_PENDING: 'errors.cancellationPending',
  PAYMENT_PENDING: 'plan.paymentPending',
  PLAN_UNCHANGED: null,
};
export function rowReasonCopy(t, code) {
  if (code && Object.prototype.hasOwnProperty.call(ROW_REASON_KEY, code)) {
    return ROW_REASON_KEY[code] ? t(ROW_REASON_KEY[code]) : null;
  }
  return code ? t('plan.unavailableWithCode', { code }) : t('plan.unavailable');
}
